import { enqueueTurn } from '@shared/constants.js';
import { Direction, ScreenState } from '@shared/types.js';
import { game } from '@client/state.js';
import type { DomEls } from '@client/state.js';
import { UnlockId, isUnlockActive } from '@client/unlocks.js';

const keyMap: Record<string, Direction> = {
  ArrowUp: Direction.UP, ArrowDown: Direction.DOWN, ArrowLeft: Direction.LEFT, ArrowRight: Direction.RIGHT,
  w: Direction.UP, W: Direction.UP, s: Direction.DOWN, S: Direction.DOWN,
  a: Direction.LEFT, A: Direction.LEFT, d: Direction.RIGHT, D: Direction.RIGHT,
};

const SWIPE_THRESHOLD = 25;
const TAP_DISTANCE = 20;
const TAP_TIME_MS = 300;

export interface InputCallbacks {
  onPause(): void;
  onResume(): void;
  onStart(): void;
  onDir(dir: Direction): void;
  onToggleNavOverlay(): void;
  onJump(): void;
}

function canJump(): boolean {
  if (!isUnlockActive(UnlockId.JUMP, game.arenaUnlock)) return false;
  // Arena: server tracks jump state; local: client tracks jumpsLeft
  return game.online || game.jumpsLeft > 0;
}

let jumpCallback: (() => void) | null = null;

function triggerJump(): void {
  if (game.state !== ScreenState.PLAYING || !canJump()) return;
  if (jumpCallback) jumpCallback();
}

export function setupInput(els: DomEls, callbacks: InputCallbacks): void {
  jumpCallback = callbacks.onJump;
  let lastKeyTime = 0;
  let lastKeyDir: Direction | null = null;
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (game.online && (game.state === ScreenState.PLAYING || game.state === ScreenState.SPECTATING || game.state === ScreenState.COUNTDOWN)) {
        callbacks.onToggleNavOverlay();
      } else if (game.state === ScreenState.PLAYING) {
        callbacks.onPause();
      } else if (game.state === ScreenState.PAUSED) {
        callbacks.onResume();
      }
      return;
    }
    if (e.key === ' ') {
      e.preventDefault();
      if (game.state === ScreenState.PAUSED) { callbacks.onResume(); return; }
      if (game.state === ScreenState.GAMEOVER || game.state === ScreenState.ARENA_GAMEOVER) {
        callbacks.onStart();
      }
      return;
    }
    if (game.state !== ScreenState.PLAYING) return;
    const dir = keyMap[e.key];
    if (!dir) return;

    // Double-tap same direction key to jump
    const now = Date.now();
    if (dir === lastKeyDir && now - lastKeyTime < TAP_TIME_MS) {
      triggerJump();
      lastKeyTime = 0;
      lastKeyDir = null;
    } else {
      lastKeyTime = now;
      lastKeyDir = dir;
    }

    if (game.online) {
      callbacks.onDir(dir);
    } else {
      steerHuman(dir);
    }
  });

  let touchStartX = 0, touchStartY = 0, touchStartTime = 0;
  let lastSwipeDir: Direction | null = null;
  const canvas = document.getElementById('game')!;

  const dpadEls: Record<Direction, HTMLElement> = {
    [Direction.UP]: document.getElementById('dpad-up')!,
    [Direction.DOWN]: document.getElementById('dpad-down')!,
    [Direction.LEFT]: document.getElementById('dpad-left')!,
    [Direction.RIGHT]: document.getElementById('dpad-right')!,
  };
  function flashDpad(dir: Direction): void {
    const el = dpadEls[dir];
    el.classList.remove('highlight');
    // Force reflow to restart animation
    void el.offsetWidth;
    el.classList.add('highlight');
    // Haptic feedback on mobile
    navigator.vibrate?.(15);
  }

  function applyDir(dir: Direction): void {
    flashDpad(dir);
    if (game.online) callbacks.onDir(dir);
    else steerHuman(dir);
  }

  canvas.addEventListener('touchstart', (e: TouchEvent) => {
    const t = e.touches[0];
    touchStartX = t.clientX;
    touchStartY = t.clientY;
    touchStartTime = Date.now();
    lastSwipeDir = null;
    e.preventDefault();
  }, { passive: false });

  canvas.addEventListener('touchmove', (e: TouchEvent) => {
    if (game.state !== ScreenState.PLAYING) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < SWIPE_THRESHOLD) return;

    const dir: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dx > 0 ? Direction.RIGHT : Direction.LEFT)
      : (dy > 0 ? Direction.DOWN : Direction.UP);

    if (dir !== lastSwipeDir) {
      lastSwipeDir = dir;
      // Reset anchor so next direction change is relative to this point
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      applyDir(dir);
    }
    e.preventDefault();
  }, { passive: false });

  canvas.addEventListener('touchend', (e: TouchEvent) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const elapsed = Date.now() - touchStartTime;

    // Tap detection (no significant movement)
    if (dist < TAP_DISTANCE && elapsed < TAP_TIME_MS) {
      // On mobile during gameplay, d-pad handles input — skip nav toggle on canvas tap
      const dpadActive = document.getElementById('dpad-area')!.classList.contains('active');
      if (game.online && (game.state === ScreenState.PLAYING || game.state === ScreenState.SPECTATING) && !dpadActive) {
        callbacks.onToggleNavOverlay(); return;
      }
      if (game.state === ScreenState.PAUSED) { callbacks.onResume(); return; }
      if (game.state === ScreenState.GAMEOVER || game.state === ScreenState.ARENA_GAMEOVER) {
        callbacks.onStart(); return;
      }
    }
    // Final swipe on release (if touchmove didn't already handle it)
    if (game.state !== ScreenState.PLAYING) return;
    if (dist < SWIPE_THRESHOLD) return;

    const dir: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dx > 0 ? Direction.RIGHT : Direction.LEFT)
      : (dy > 0 ? Direction.DOWN : Direction.UP);

    if (dir !== lastSwipeDir) applyDir(dir);
    e.preventDefault();
  }, { passive: false });

  function handleOverlayActivate(e: Event): void {
    if (e.target !== els.overlay && e.target !== els.message && e.target !== els.subMessage) return;
    e.preventDefault();
    if (game.state === ScreenState.PAUSED) callbacks.onResume();
    else if (game.state === ScreenState.GAMEOVER || game.state === ScreenState.ARENA_GAMEOVER) callbacks.onStart();
  }
  els.overlay.addEventListener('click', handleOverlayActivate);
  els.overlay.addEventListener('touchend', handleOverlayActivate);

  function handlePause(e: Event): void {
    e.stopPropagation(); e.preventDefault();
    if (game.state === ScreenState.PLAYING) callbacks.onPause();
  }
  els.pauseBtn.addEventListener('click', handlePause);
  els.pauseBtn.addEventListener('touchend', handlePause);

  // D-pad: hit-test which button the finger is over. Buttons cover the entire
  // dpad-area (gap: 0), so position alone is authoritative — interpreting drift
  // as a swipe causes a tap-near-edge to flip to the opposite direction.
  const dpadArea = document.getElementById('dpad-area')!;
  let dpadLastDir: Direction | null = null;

  function dirFromDpadTouch(clientX: number, clientY: number): Direction | null {
    for (const [dir, el] of Object.entries(dpadEls) as Array<[Direction, HTMLElement]>) {
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
        return dir;
      }
    }
    return null;
  }

  let lastDpadTapTime = 0;
  let lastDpadTapDir: Direction | null = null;

  dpadArea.addEventListener('touchstart', (e: TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (game.state !== ScreenState.PLAYING) return;

    const t = e.changedTouches[0];
    const dir = dirFromDpadTouch(t.clientX, t.clientY);

    // Double-tap detection for jump — same direction only
    const now = Date.now();
    if (dir && dir === lastDpadTapDir && now - lastDpadTapTime < TAP_TIME_MS) {
      triggerJump();
      lastDpadTapTime = 0;
      lastDpadTapDir = null;
    } else {
      lastDpadTapTime = now;
      lastDpadTapDir = dir;
    }

    if (dir) {
      dpadLastDir = dir;
      applyDir(dir);
    } else {
      dpadLastDir = null;
    }
  }, { passive: false });

  dpadArea.addEventListener('touchmove', (e: TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (game.state !== ScreenState.PLAYING) return;
    const t = e.changedTouches[0];
    const dir = dirFromDpadTouch(t.clientX, t.clientY);
    if (dir && dir !== dpadLastDir) {
      dpadLastDir = dir;
      applyDir(dir);
    }
  }, { passive: false });

}

function steerHuman(dir: Direction): void {
  const human = game.players.find(p => !p.isBot && p.alive);
  // Buffer the turn (depth-limited) so rapid local sequences execute in order,
  // matching the server's input queue. Drained one per tick in localTick.
  if (human) enqueueTurn(game.localDirQueue, human.dir, dir);
}
